import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { RoboticSessionType, UpdateRobosignStatusResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';

import { updateProgressCache } from './progressCacheUtils';


/**
 * Input type for updating robosign session status
 */
export interface UpdateRobosignStatusInput {
	status: string;
	sessionType: RoboticSessionType;
}

/**
 * API function to update robotic signing session status
 * Updates status in purchasedVehicle.esigs.roboticSigningSignatures.session or counterSigningSession
 */
export const updateRobosignStatus = async (
	servicesProviderId: string,
	orderId: string,
	input: UpdateRobosignStatusInput,
): Promise<UpdateRobosignStatusResponse> => {
	const response = await apiClient.post<UpdateRobosignStatusResponse>(
		ordersEndpoints.updateRobosignStatus(servicesProviderId, orderId),
		input,
	);
	return response.data;
};

/**
 * Hook for updating robotic signing session status
 * @param orderId - The purchased vehicle ID
 */
export const useUpdateRobosignStatus = (orderId: string | undefined) => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: UpdateRobosignStatusInput) => {
			if (!orderId) {
				throw new Error('Unable to update robosign status without an order ID.');
			}
			if (!servicesProviderId) {
				throw new Error('Unable to update robosign status without a services provider ID.');
			}
			return updateRobosignStatus(servicesProviderId, orderId, input);
		},
		onSuccess: (data) => {
			if (!orderId || !servicesProviderId) return;
			updateProgressCache(queryClient, servicesProviderId, orderId, data.progress);
		},
	});
};
